// Potenzialanalyse (nur Admin) — bewusst OHNE Kunde, Projekt oder Ads-Konto.
//
// Für die Frage im Kundengespräch: „Lohnt sich Badsanierung in Kassel bei
// 20 €/Tag?" Frei eintippen, das System löst sie in echte Zahlen auf
// (Keyword Planner für die Region) und schreibt eine Einschätzung daraus.
//
// Absichtlich eine eigene Seite statt eines Panels im Analytics-Tab: Ein
// Interessent hat weder Projekt noch Konto, an dem so ein Panel hängen könnte.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Sparkles, Search, FileDown } from 'lucide-react';
import toast from 'react-hot-toast';
import PdfVorschau from '@/components/PdfVorschau';
import { fetchPotenzialPdfUrl, openPotenzialPdf, runPotenzialAnalyse, type PotenzialErgebnis } from '@/lib/analytics-api';

const BEISPIELE = [
  'Lohnt sich Badsanierung in Kassel bei 20 €/Tag?',
  'Welche Kampagnen würden für einen Tischler in Fritzlar, 40 km Umkreis, Sinn ergeben?',
  'Fliesenleger in Damme — reichen 10 €/Tag für spürbare Anfragen?',
];

export default function PotenzialPage() {
  const [frage, setFrage] = useState('');
  const [ergebnis, setErgebnis] = useState<PotenzialErgebnis | null>(null);

  const mut = useMutation({
    mutationFn: (f: string) => runPotenzialAnalyse(f),
    onSuccess: (d) => setErgebnis(d),
    onError: (e: any) => toast.error(e.message),
  });

  const pdfMut = useMutation({
    mutationFn: () => openPotenzialPdf(ergebnis!),
    onError: (e: any) => toast.error(e.message),
  });

  const starten = () => {
    if (!frage.trim()) { toast.error('Bitte eine Frage eingeben'); return; }
    mut.mutate(frage.trim());
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Potenzialanalyse</h1>
        <p className="mt-0.5 text-sm text-gray-500">
          Frei fragen, echte Zahlen bekommen — Suchvolumina und Klickpreise für die Region direkt aus dem Google
          Keyword Planner. Braucht weder Kunde noch Ads-Konto.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <textarea
          value={frage}
          onChange={(e) => setFrage(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) starten(); }}
          rows={3}
          placeholder="z. B. Lohnt sich Badsanierung in Kassel bei 20 €/Tag?"
          className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {BEISPIELE.map((b) => (
              <button
                key={b}
                onClick={() => setFrage(b)}
                className="rounded-lg border border-gray-200 px-2 py-1 text-[11px] text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              >
                {b}
              </button>
            ))}
          </div>
          <button
            onClick={starten}
            disabled={mut.isPending}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {mut.isPending ? 'Analysiere…' : 'Analysieren'}
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-gray-400">
          Ort ist Pflicht — ohne Region gibt es keine Suchvolumina. Umkreis und Budget nimmt das System aus der
          Frage, sonst 40 km und 20 €/Tag. ⌘/Strg + Enter startet.
        </p>
      </div>

      {mut.isPending && (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white py-10 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Keyword Planner wird abgefragt und ausgewertet…
        </div>
      )}

      {ergebnis && !mut.isPending && (
        <div className="space-y-4">
          {ergebnis.hinweis && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {ergebnis.hinweis}
            </p>
          )}

          {/* Kopfzahlen: was das Budget in dieser Region kauft */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Ort · Umkreis', wert: `${ergebnis.parameter.ort} · ${ergebnis.parameter.umkreis_km} km` },
              { label: 'Budget', wert: `${ergebnis.parameter.budget_eur_pro_tag} €/Tag` },
              {
                label: 'Klickpreis Ø',
                wert: ergebnis.cpc_schnitt_eur != null ? `${ergebnis.cpc_schnitt_eur.toFixed(2)} €` : '–',
              },
              {
                // Klicks, die das Budget kauft — harte Rechnung, keine Schätzung.
                label: 'Klicks fürs Budget',
                wert: ergebnis.phase.klicks_budget != null
                  ? `${new Intl.NumberFormat('de-DE').format(ergebnis.phase.klicks_budget)}/Mon.`
                  : '–',
              },
              {
                label: ergebnis.phase.nachfrage_geschaetzt ? 'Anfragen (geschätzt)' : 'Erwartete Anfragen',
                wert: ergebnis.phase.conversions_pro_monat != null ? `${ergebnis.phase.conversions_pro_monat}/Mon.` : '–',
              },
            ].map((k) => (
              <div key={k.label} className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                <p className="text-[10px] uppercase tracking-wider text-gray-400">{k.label}</p>
                <p className="mt-0.5 text-lg font-semibold text-gray-900">{k.wert}</p>
              </div>
            ))}
          </div>

          <p className="text-xs leading-relaxed text-gray-500">
            {ergebnis.phase.begruendung}
            {ergebnis.region && <span className="text-gray-400"> · Region laut Google: {ergebnis.region}</span>}
            {ergebnis.planner_konto && <span className="text-gray-400"> · Abfrage über {ergebnis.planner_konto}</span>}
          </p>

          {/* Die Analyse IST das PDF — genau wie beim Monatsbericht. */}
          <div className="flex flex-col items-center gap-2 rounded-lg border border-gray-200 bg-white px-5 py-6">
            <PdfVorschau
              breite={420}
              titel={`Potenzialanalyse ${ergebnis.parameter.ort}`}
              laden={() => fetchPotenzialPdfUrl(ergebnis)}
              herunterladen={() => openPotenzialPdf(ergebnis)}
            />
            <div className="flex items-center gap-3">
              <p className="text-[11px] text-gray-400">Zum Vergrößern auf das Blatt klicken</p>
              <button
                onClick={() => { pdfMut.mutate(); }}
                disabled={pdfMut.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {pdfMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
                Herunterladen
              </button>
            </div>
          </div>

          {ergebnis.keyword_ideen.length > 0 && (
            <details className="rounded-lg border border-gray-200 bg-white">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50">
                <Search className="mr-1.5 inline h-3.5 w-3.5 text-gray-400" />
                Alle {ergebnis.keyword_ideen.length} recherchierten Begriffe
              </summary>
              <div className="max-h-96 overflow-y-auto border-t border-gray-100">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="text-left text-[10px] uppercase text-gray-400">
                      <th className="px-4 py-1.5 font-medium">Begriff</th>
                      <th className="px-2 py-1.5 text-right font-medium">Ø Suchen/Mon.</th>
                      <th className="px-2 py-1.5 text-right font-medium">Klickpreis</th>
                      <th className="px-4 py-1.5 text-right font-medium">Wettbewerb</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {ergebnis.keyword_ideen.map((k, i) => (
                      <tr key={i}>
                        <td className="px-4 py-1 text-gray-800">{k.text}</td>
                        <td className="px-2 py-1 text-right tabular-nums text-gray-600">
                          {k.suchvolumen_monat != null ? new Intl.NumberFormat('de-DE').format(k.suchvolumen_monat) : '–'}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums text-gray-500">
                          {k.cpc_min_eur != null && k.cpc_max_eur != null
                            ? `${k.cpc_min_eur.toFixed(2)}–${k.cpc_max_eur.toFixed(2)} €`
                            : '–'}
                        </td>
                        <td className="px-4 py-1 text-right text-gray-400">
                          {k.wettbewerb === 'HIGH' ? 'hoch' : k.wettbewerb === 'MEDIUM' ? 'mittel' : k.wettbewerb === 'LOW' ? 'niedrig' : '–'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
